import {
  externalDepthCmFromDepthM,
  PRESTIGE_EXTERNAL_WIDTHS_CM,
  PRESTIGE_WIDTH_VALUE_MAX,
  PRESTIGE_WIDTH_VALUE_MIN,
} from '@domain/constants/prestigeDimensions';

export type UnitType = 'cm' | 'inches';

/**
 * External pergola width in cm for a given `widthValue`. Looked up
 * straight from the sheet axis (`Pergola - Price` row 4); both 1-room
 * and 2-room sections are sheet-exact.
 */
export const getPrestigeExternalWidthCm = (widthValue: number): number => {
  const index = Math.min(
    Math.max(widthValue - PRESTIGE_WIDTH_VALUE_MIN, 0),
    PRESTIGE_WIDTH_VALUE_MAX - PRESTIGE_WIDTH_VALUE_MIN,
  );
  return PRESTIGE_EXTERNAL_WIDTHS_CM[index];
};

export const getPrestigeExternalDepthCm = (depthValue: number): number =>
  externalDepthCmFromDepthM(depthValue);

export const getPrestigeExternalHeightCm = (heightValue: number): number =>
  Math.round((heightValue * 100 - 3.4) * 10) / 10;

const cmToFeetInches = (cm: number): string => {
  const totalInches = cm / 2.54;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);

  if (inches === 12) {
    feet += 1;
    inches = 0;
  }

  return inches === 0 ? `${feet}'` : `${feet}' ${inches}"`;
};

export const formatDimensionCm = (cm: number, unit: UnitType): string => {
  if (unit === 'inches') return cmToFeetInches(cm);
  return `${Math.round(cm * 10) / 10} cm`;
};

/**
 * Parse a free-typed dimension into an external measurement in cm.
 *
 * `cm` unit  → accepts "310", "310 cm", "310.5".
 * `inches`   → accepts feet/inches in many shapes: `10' 2"`, `10'2`, `10' `,
 *              `10 ft 2 in`, `10 2` (feet then inches), or `122"` / `122 in`
 *              (inches only). A bare number is treated as feet, matching the
 *              feet-first display.
 *
 * Returns null when nothing numeric can be extracted.
 */
export const parseDimensionInputToCm = (
  raw: string,
  unit: UnitType,
): number | null => {
  const s = raw.trim().toLowerCase().replace(/[”″]/g, '"').replace(/[’′]/g, "'");
  if (!s) return null;

  if (unit === 'cm') {
    const n = parseFloat(s.replace(/cm/g, '').trim());
    return Number.isFinite(n) ? n : null;
  }

  const feetMatch = s.match(/(-?\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)/);
  const inchMatch = s.match(/(-?\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)/);

  let feet = 0;
  let inches = 0;

  if (feetMatch || inchMatch) {
    if (feetMatch) feet = parseFloat(feetMatch[1]);
    if (inchMatch) inches = parseFloat(inchMatch[1]);
  } else {
    const nums = s.match(/-?\d+(?:\.\d+)?/g);
    if (!nums || nums.length === 0) return null;
    feet = parseFloat(nums[0]);
    if (nums.length > 1) inches = parseFloat(nums[1]);
  }

  const cm = (feet * 12 + inches) * 2.54;
  return Number.isFinite(cm) ? cm : null;
};

/**
 * Given the list of valid internal step values and a mapping to their external
 * cm size, return the step whose external size is closest to `targetCm`.
 */
export const findNearestStepByExternalCm = (
  steps: readonly number[],
  toExternalCm: (stepValue: number) => number,
  targetCm: number,
): number | null => {
  let best: number | null = null;
  let bestDiff = Infinity;
  for (const step of steps) {
    const diff = Math.abs(toExternalCm(step) - targetCm);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = step;
    }
  }
  return best;
};
